import { SMART_COURSE_PALETTE } from './smartCourseColors'
import { PERIOD_TIMES, NIGHT_START } from '../data/periodTimes'
import { getRuntimeCourseCatalog, getRuntimeCatalogMeta, findRuntimeCatalogCourse } from '../services/runtimeCourseCatalog'
import { REQUIREMENT_DATA, CREDIT_RULES } from '../data/requirementData'
import {
  getCourseTag, getDeptReasonLabel, filterCoursesByDept, getEligibleSectionsForStudent, isExactDeptMatch,
  isCourseAvailableForGrade, isCourseAvailableForCollege,
} from './deptUtils'
import { getCourseScheduleMode } from './courseScheduleMode'

export const GRID_DAYS  = ['월', '화', '수', '목', '금']
export const MAX_PERIOD = 13
export const DAY_PERIODS = [1, 2, 3, 4, 5, 6, 7, 8]
export const ALL_PERIODS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]

// ── 이수요건 자동 생성 (courseData의 전공필수 과목 기반) ───────────────────────
export function generateRequirementFromCourseData(department, grade) {
  if (!department) return null

  // 정확한 학과명 일치로 내 학과 과목 추출 (키워드 매칭 사용 시 상위 단과대학 과목이 혼입됨)
  const deptCourses = getRuntimeCourseCatalog().filter(c => (c.department ?? '').trim() === department.trim())
  if (deptCourses.length === 0) return null

  // subjectKey 기준 중복 제거 후 전공필수 과목 수집
  const seen = new Set()
  const requiredSubjects = []
  for (const c of deptCourses) {
    if (c.category !== '전공필수') continue
    if (!c.subjectKey || seen.has(c.subjectKey)) continue
    seen.add(c.subjectKey)
    requiredSubjects.push({
      subjectKey:     c.subjectKey,
      displayName:    c.name,
      category:       '전공필수',
      credit:         c.credit ?? 3,
      note:           '',
      _autoGenerated: true,
    })
  }

  if (requiredSubjects.length === 0) return null

  const totalReqCr = requiredSubjects.reduce((s, r) => s + r.credit, 0)

  return {
    id:                        `auto-${department}-${grade ?? 'any'}`,
    department,
    grade:                     grade ?? null,
    admissionYear:             null,
    status:                    'auto',
    note:                      '실제 개설강좌 전공필수 과목 기반으로 자동 생성된 데이터입니다. 학과 교육과정표를 반드시 확인하세요.',
    requiredSubjects,
    electiveCategories:        ['교양선택', '핵심교양', '교양필수', '기초교양', '일반선택', '이러닝'],
    recommendedElectiveCredits: Math.max(0, 21 - totalReqCr),
    maxCredit:                 21,
    minCredit:                 15,
  }
}

// ── 이수요건 조회 (수동 데이터 우선, 없으면 자동 생성) ───────────────────────
// department가 없을 때(예: 1학년 college-only 프로필)는 college로 fallback한다. 단, college
// 안에 학과별로 서로 다른 커리큘럼 레코드가 여러 개 섞여 있으면(예: 지능형SW대학, 혁신공과대학)
// 어느 학과 걸 대표로 써야 할지 알 수 없으므로 절대 임의로 첫 레코드를 반환하지 않는다 —
// admissionYear까지 포함해 department가 정확히 하나로 좁혀질 때만 반환한다.
export function getRequirementByStudent(department, grade, admissionYear, semester = null, college = null) {
  const semMatch = r => semester == null || r.semester == null || r.semester === semester

  if (department) {
    if (admissionYear) {
      const exact = REQUIREMENT_DATA.find(r =>
        r.department === department &&
        r.grade      === grade      &&
        r.admissionYear === admissionYear &&
        semMatch(r)
      )
      if (exact) return exact
    }

    const byGrade = REQUIREMENT_DATA.find(r =>
      r.department === department &&
      r.grade      === grade      &&
      semMatch(r)
    )
    if (byGrade) return byGrade

    // courseData에서 자동 생성 (수동 데이터 없는 모든 학과 대응)
    return generateRequirementFromCourseData(department, grade)
  }

  if (college) {
    const candidates = REQUIREMENT_DATA.filter(r => r.collegeName === college && r.grade === grade && semMatch(r))
    if (candidates.length === 0) return null
    if (admissionYear) {
      const exactYear = candidates.filter(r => r.admissionYear === admissionYear)
      const distinctDepts = new Set(exactYear.map(r => r.department))
      return (exactYear.length > 0 && distinctDepts.size === 1) ? exactYear[0] : null
    }
    const distinctDepts = new Set(candidates.map(r => r.department))
    const distinctYears = new Set(candidates.map(r => r.admissionYear))
    return (distinctDepts.size === 1 && distinctYears.size === 1) ? candidates[0] : null
  }

  return null
}

// ── 이수요건 과목 매칭 통계 ───────────────────────────────────────────────────
/**
 * requirement의 각 필수과목이 COURSE_DATA에 개설강좌가 있는지 확인.
 * @returns {{ matched: number, unmatched: number, total: number, unmatchedNames: string[] }}
 */
export function getRequirementMatchStats(requirement) {
  if (!requirement?.requiredSubjects?.length) {
    return { matched: 0, unmatched: 0, total: 0, unmatchedNames: [] }
  }
  let matched = 0
  const unmatchedNames = []
  for (const rs of requirement.requiredSubjects) {
    const catalog = getRuntimeCourseCatalog()
    const found = rs.subjectKey
      ? catalog.some(c => c.subjectKey === rs.subjectKey)
      : catalog.some(c => c.name?.includes(rs.displayName))
    if (found) matched++
    else unmatchedNames.push(rs.displayName)
  }
  return {
    matched,
    unmatched: unmatchedNames.length,
    total:     requirement.requiredSubjects.length,
    unmatchedNames,
  }
}

// ── 분반 목록 조회 ─────────────────────────────────────────────────────────────
export function getCourseSectionsBySubject(subjectKey) {
  return getRuntimeCourseCatalog().filter(c => c.subjectKey === subjectKey)
}

// ── 한글 초성 추출 ────────────────────────────────────────────────────────────
const HANGUL_CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ']

/** 문자열의 한글을 초성으로 변환(비한글은 소문자 유지). 예: "왕홍위" → "ㅇㅎㅇ" */
export function extractChosung(str) {
  if (!str) return ''
  let out = ''
  for (const ch of String(str)) {
    const code = ch.charCodeAt(0)
    if (code >= 0xAC00 && code <= 0xD7A3) {
      out += HANGUL_CHO[Math.floor((code - 0xAC00) / 588)]
    } else {
      out += ch.toLowerCase()
    }
  }
  return out
}

// 검색용 정규화: 공백 제거 + 소문자 + 한글/초성자모(ㄱ-ㅎ)/영숫자만 유지
function normalizeSearchText(str) {
  return String(str ?? '').replace(/\s+/g, '').toLowerCase().replace(/[^가-힣ㄱ-ㅎa-z0-9]/g, '')
}
// 영숫자만 추출 (예: "AI활용CSL1" → "aicsl1") — "aicsl" 같은 압축 검색 지원
function asciiOnly(str) {
  return String(str ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

// 공통교양 카테고리 (other-dept 감점을 약하게 적용)
const GENED_CATEGORIES = new Set(['기초교양', '교양선택', '교양필수', '핵심교양', '영역교양'])

/**
 * 폭넓은 검색 키워드 여부 — CSL/ESL/AI활용/글쓰기/초성/교수명(한글 2~3자)이면
 * 공통교양 후보를 더 많이(15개) 노출한다.
 */
export function isBroadCourseQuery(query) {
  const q = normalizeSearchText(query)
  if (!q) return false
  if (q.includes('csl') || q.includes('esl') || q.includes('글쓰기') || q.includes('ai활용')) return true
  if (/^[ㄱ-ㅎ]+$/.test(q)) return true       // 순수 초성 검색 → 교수명/과목 폭넓게
  if (/^[가-힣]{2,3}$/.test(q)) return true     // 한글 2~3자 → 교수명 추정
  return false
}

// ── 과목명 자동완성 검색 (직접 추가 시트용) ─────────────────────────────────────
// hard-filter 없이 전체 courseData에서 검색하고 soft-rank 로 정렬한다.
// (학과·학년·단과대 hard-filter 금지 — department는 개설학과명일 뿐이고 targetGrade도 soft-rank
// 가산으로만 쓴다. selectedDept/selectedGrade/selectedCollege 불일치 후보도 제거하지 않는다.)
// 검색 대상: 과목명·교수명·분반·학과·카테고리 / 영문 대소문자·공백 무시 / 초성 검색 지원
let _courseSearchIndex = null
let _courseSearchIndexVersionKey = null

if (typeof window !== 'undefined') {
  window.addEventListener('suguide:runtime-catalog-updated', () => {
    _courseSearchIndex = null
    _courseSearchIndexVersionKey = null
  })
}

export function invalidateCourseSearchIndex() {
  _courseSearchIndex = null
  _courseSearchIndexVersionKey = null
}

function getCourseSearchIndex() {
  const meta = getRuntimeCatalogMeta()
  const currentKey = `${meta?.version ?? ''}:${meta?.hash ?? ''}:${meta?.count ?? 0}`
  if (_courseSearchIndex && _courseSearchIndexVersionKey === currentKey) {
    return _courseSearchIndex
  }
  const catalog = getRuntimeCourseCatalog()
  _courseSearchIndex = catalog.map(c => {
    const name = c.name ?? ''
    const prof = c.professor ?? ''
    const subjectKey = String(c.subjectKey ?? c.courseCode ?? '').trim()
    const courseCode = String(c.courseCode ?? c.subjectKey ?? '').trim()
    const combined = [name, prof, c.division, c.department, c.category, subjectKey, courseCode].filter(Boolean).join(' ')
    return {
      c,
      nameNorm:      normalizeSearchText(name),
      nameAscii:     asciiOnly(name),
      profNorm:      normalizeSearchText(prof),
      subjectKeyNorm: normalizeSearchText(subjectKey),
      combinedNorm:  normalizeSearchText(combined),
      combinedAscii: asciiOnly(combined),
      chosungName:   extractChosung(name),
      chosungProf:   extractChosung(prof),
    }
  })
  _courseSearchIndexVersionKey = currentKey
  return _courseSearchIndex
}

// 후보 점수: 학수번호/과목코드 + 이름정확도 + 교수명(강가산) + 초성 + 분반/학과 fallback + 완성도 + 학과/학년/단과대 가산
function scoreCourseSuggestion(e, q, selectedDept, selectedGrade, selectedCollege) {
  const { c } = e
  let score = 0
  let matched = false

  // 학수번호/과목코드 정확도
  if (e.subjectKeyNorm) {
    if (e.subjectKeyNorm === q)            { score += 110; matched = true }
    else if (e.subjectKeyNorm.startsWith(q)) { score += 90; matched = true }
    else if (e.subjectKeyNorm.includes(q))   { score += 75; matched = true }
  }

  // 과목명 정확도 우선 (시작 > 부분 > 압축영문)
  if (e.nameNorm.startsWith(q))      { score += 100; matched = true }
  else if (e.nameNorm.includes(q))   { score += 60;  matched = true }
  else if (e.nameAscii.includes(q))  { score += 55;  matched = true }

  // 교수명 일치 강하게 가산 (이름 일치 위에 추가 가산)
  if (e.profNorm) {
    if (e.profNorm === q)            { score += 95; matched = true }
    else if (e.profNorm.startsWith(q)) { score += 80; matched = true }
    else if (e.profNorm.includes(q))   { score += 70; matched = true }
  }

  // 초성 일치 가산 (name/professor 대상)
  if (e.chosungName.includes(q)) { score += 30; matched = true }
  if (e.chosungProf.includes(q)) { score += 55; matched = true }

  // 분반/학과/카테고리 fallback
  if (!matched && (e.combinedNorm.includes(q) || e.combinedAscii.includes(q))) {
    score += 22; matched = true
  }

  if (!matched) return -Infinity

  // 시간/교수/강의실 완성도 가산
  const placeable = getCourseScheduleMode(c) !== 'online' && c.startPeriod != null && c.endPeriod != null
  score += placeable ? 10 : -40
  if (c.professor) score += 6
  if (c.room) score += 4

  // 학과 가산 (hard-filter 아님: other-dept도 숨기지 않고 후순위로만)
  // my-dept 안에서도 정확히 내 학과인 분반(예: 패션디자인)을 같은 학부의 다른 전공 분반
  // (커뮤니케이션디자인·공예디자인)보다 우선한다 — 070/071 같은 분반번호가 아니라
  // getCourseTag/isExactDeptMatch(학과·수강대상 메타데이터 기준)로 판단한다.
  if (selectedDept) {
    const tag = getCourseTag(c, selectedDept)
    if (tag === 'my-dept') score += isExactDeptMatch(c, selectedDept) ? 80 : 60
    else if (tag === 'open' || tag === 'online') score += 8
    else if (tag === 'other-dept') score += GENED_CATEGORIES.has(c.category) ? -5 : -15  // 공통교양은 약하게
  }

  // 학년 가산 (hard-filter 아님: targetGrade 불일치도 제거하지 않고 후순위로만)
  if (selectedGrade != null) {
    score += isCourseAvailableForGrade(c, selectedGrade) ? 15 : -8
  }

  // 단과대 가산 — 학과가 선택돼 있으면 위 학과 가산이 이미 더 정밀하게 처리하므로,
  // 학과 미확정(1학년 등 college-only 프로필)일 때만 보조 신호로 쓴다.
  if (!selectedDept && selectedCollege) {
    score += isCourseAvailableForCollege(c, selectedCollege) ? 40 : -8
  }

  return score
}

export function searchOfferedCourses(query, limit = 8, selectedDept = null, selectedGrade = null, selectedCollege = null) {
  const q = normalizeSearchText(query)
  if (q.length < 1) return []   // 1글자 이상부터 (공백만 입력 시 q가 비어 후보 없음)
  const scored = []
  for (const e of getCourseSearchIndex()) {
    const s = scoreCourseSuggestion(e, q, selectedDept, selectedGrade, selectedCollege)
    if (s > -Infinity) scored.push({ c: e.c, s })
  }
  scored.sort((a, b) => {
    if (b.s !== a.s) return b.s - a.s
    if (a.c.name !== b.c.name) return a.c.name.localeCompare(b.c.name)
    // 같은 과목명: 정원 작은(희소) 분반 우선 → 분반 번호 순
    const ca = a.c.capacity ?? 9999, cb = b.c.capacity ?? 9999
    if (ca !== cb) return ca - cb
    return String(a.c.division ?? '').localeCompare(String(b.c.division ?? ''))
  })
  return scored.slice(0, limit).map(e => e.c)
}

// ── 교시(period) 기반 시간 계산 (직접 추가/수정 전용) ──────────────────────────
// 수원대 실제 교시표(50/45분) 기준. 직접 추가는 60분 단위가 아니라 교시 단위로 계산한다.
function _periodToMin(t) {
  const [h, m] = String(t ?? '').split(':').map(Number)
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0)
}

// 정렬된 교시 시작 시각 목록 (CourseEditSheet 시작 시간 옵션용)
export const PERIOD_START_OPTIONS = Object.keys(PERIOD_TIMES)
  .map(Number).sort((a, b) => a - b)
  .map(p => PERIOD_TIMES[p].start)

// 시각 → 가장 가까운 교시 번호
export function nearestPeriodForTime(timeStr) {
  const target = _periodToMin(timeStr)
  let best = 1, bestDiff = Infinity
  for (const p of Object.keys(PERIOD_TIMES).map(Number)) {
    const diff = Math.abs(_periodToMin(PERIOD_TIMES[p].start) - target)
    if (diff < bestDiff) { bestDiff = diff; best = p }
  }
  return best
}

// 시작 교시 + 길이(교시 수) → 교시표 기준 블록 정보
// 예: 5교시 시작 + 3교시 길이 → 13:30~16:20 (16:30 아님)
export function periodBlockFromStart(startPeriod, lengthPeriods) {
  const sp = Math.min(Math.max(Number(startPeriod) || 1, 1), MAX_PERIOD)
  const len = Math.min(4, Math.max(1, Number(lengthPeriods) || 1))
  const ep = Math.min(MAX_PERIOD, sp + len - 1)
  return {
    startPeriod: sp,
    endPeriod: ep,
    lengthPeriods: ep - sp + 1,
    startTimeStr: PERIOD_TIMES[sp]?.start ?? '09:30',
    endTimeStr: PERIOD_TIMES[ep]?.end ?? PERIOD_TIMES[sp]?.end ?? '10:20',
  }
}

// 같은 요일 다른 과목/교시 한계에 막히지 않는 최대 길이(교시 수). 1~4 클램프.
export function maxPeriodLengthFree(startPeriod, day, allCourses = [], selfId = null) {
  const sp = Math.min(Math.max(Number(startPeriod) || 1, 1), MAX_PERIOD)
  let maxEndPeriod = MAX_PERIOD
  for (const o of allCourses) {
    if (!o || o.id === selfId || getCourseScheduleMode(o) === 'online') continue
    const days = Array.isArray(o.days) ? o.days : (o.day ? [o.day] : [])
    if (!days.includes(day)) continue
    if (o.startPeriod != null && o.startPeriod > sp && o.startPeriod - 1 < maxEndPeriod) {
      maxEndPeriod = o.startPeriod - 1
    }
  }
  return Math.min(4, Math.max(1, maxEndPeriod - sp + 1))
}

// ── 과목명 / 과목코드 정규화 ──────────────────────────────────────────────────

/** 공백·특수문자 제거 후 소문자 변환 — 이름 매칭 3순위용 */
export function normalizeCourseName(name) {
  if (!name) return ''
  return String(name).replace(/\s+/g, '').replace(/[^가-힣a-zA-Z0-9]/g, '').toLowerCase()
}

/** 과목코드 앞뒤 공백 제거 + 0 패딩 정규화 (예: '546' → '00546') */
export function normalizeCourseCode(code) {
  if (!code) return ''
  const s = String(code).trim()
  return s.padStart(5, '0')
}

/**
 * 저장용 과목명 정리 — "마이크로디그리" 문구만 제거한다 (분반/전용반 정보는 유지).
 * 예: "경영학원론(1-5전용반_마이크로디그리)" → "경영학원론(1-5전용반)"
 *     "경영학원론(마이크로디그리)" → "경영학원론"
 * 시간표에 과목을 추가/저장하는 모든 경로(직접 추가, 자동완성, OCR, 불러오기)에서 사용한다.
 */
export function normalizeCourseNameForStorage(name) {
  if (!name) return name ?? ''
  const str = String(name)
  if (!str.includes('마이크로디그리')) return str

  return str
    .replace(/_?마이크로디그리_?/g, '')
    .replace(/_{2,}/g, '_')
    .replace(/([\(（])[_\s]+/g, '$1')
    .replace(/[_\s]+([\)）])/g, '$1')
    .replace(/[\(（]\s*[\)）]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/**
 * 표시용 짧은 과목명 — 마이크로디그리 제거 후 괄호 안 분반/전용반 메타 정보까지 제거한다.
 * 홈/셔틀 추천/수기 탑승 모드처럼 공간이 좁은 곳에서 사용한다.
 * 예: "경영학원론(1-5전용반_마이크로디그리)" → "경영학원론"
 *     "AI활용CSL1" → "AI활용CSL1"
 */
export function getShortCourseDisplayName(name) {
  if (!name) return ''
  return normalizeCourseNameForStorage(name)
    .replace(/\s*[\(（][^)）]*[\)）]/g, '')
    .trim()
}

export const PLAN_B_FIXED_COURSE_NAMES = ['경영학원론', '경제학원론']

export function isPlanBFixedCourse(courseOrName) {
  const name = typeof courseOrName === 'string' ? courseOrName : courseOrName?.name
  const normalized = getShortCourseDisplayName(name).replace(/\s+/g, '')
  return PLAN_B_FIXED_COURSE_NAMES.some(fixed => normalized === fixed.replace(/\s+/g, ''))
}

// ── 필수과목 ↔ courseData 매칭 (3단계 우선순위) ────────────────────────────────

/**
 * 필수과목 목록을 courseData와 매칭한다.
 *
 * 매칭 우선순위:
 *   1순위: courseCode(subjectKey) 완전일치
 *   2순위: 정규화된 과목명 완전일치
 *   3순위: 공백/특수문자 제거 후 과목명 일치
 *
 * @param {object[]} requiredCourses  — ALL_REQUIREMENTS[].requiredCourses 형식
 * @param {object[]} courseData       — COURSE_DATA 배열
 * @returns {{ matchedRequirements: object[], unmatchedRequirements: object[], matchedCourseCount: number, unmatchedCourseCount: number }}
 */
export function matchRequirementsWithCourses(requiredCourses, courseData) {
  if (!Array.isArray(requiredCourses) || !Array.isArray(courseData)) {
    return { matchedRequirements: [], unmatchedRequirements: [], matchedCourseCount: 0, unmatchedCourseCount: 0 }
  }
  const matched   = []
  const unmatched = []

  for (const rc of requiredCourses) {
    const code = normalizeCourseCode(rc.courseCode ?? rc.subjectKey ?? '')
    const nameNorm = normalizeCourseName(rc.courseName ?? rc.displayName ?? '')

    let sections = []

    // 1순위: 과목코드 완전일치
    if (code) {
      sections = courseData.filter(c => normalizeCourseCode(c.subjectKey) === code)
    }

    // 2순위: 정규화 과목명 완전일치
    if (!sections.length && nameNorm) {
      sections = courseData.filter(c => normalizeCourseName(c.name) === nameNorm)
    }

    // 3순위: matchKeywords 포함 검색
    if (!sections.length && rc.matchKeywords?.length) {
      sections = courseData.filter(c =>
        rc.matchKeywords.some(kw => c.name?.includes(kw))
      )
    }

    const entry = { ...rc, sections, matched: sections.length > 0 }
    if (sections.length > 0) matched.push(entry)
    else unmatched.push(entry)
  }

  return {
    matchedRequirements:  matched,
    unmatchedRequirements: unmatched,
    matchedCourseCount:   matched.length,
    unmatchedCourseCount: unmatched.length,
  }
}

// ── 충돌 검사 ──────────────────────────────────────────────────────────────────
export function checkTimeConflict(c1, c2) {
  if (!c1 || !c2 || c1 === c2 || (c1.id && c1.id === c2.id) || getCourseScheduleMode(c1) === 'online' || getCourseScheduleMode(c2) === 'online') return false
  return courseMeetings(c1).some(a => courseMeetings(c2).some(b => {
    if (!a.days.some(day => b.days.includes(day))) return false
    return a.start < b.end && b.start < a.end
  }))
}

function timeToMinutes(value) {
  if (typeof value !== 'string') return null
  const [hours, minutes] = value.split(':').map(Number)
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null
}

// 교시 배열을 연속 구간으로 쪼갠다 — [1,2,3,5,6] → [{start:1,end:3},{start:5,end:6}]
function contiguousPeriodRuns(periods) {
  const sorted = [...new Set(periods)].filter(Number.isInteger).sort((a, b) => a - b)
  const runs = []
  for (const period of sorted) {
    const last = runs[runs.length - 1]
    if (last && period === last.end + 1) last.end = period
    else runs.push({ start: period, end: period })
  }
  return runs
}

function courseMeetings(course) {
  const rawMeetings = Array.isArray(course.meetings) && course.meetings.length ? course.meetings : [course]
  return rawMeetings.flatMap(meeting => {
    const days = Array.isArray(meeting.days) ? meeting.days.filter(Boolean) : (meeting.day ? [meeting.day] : [])
    if (!days.length) return []
    const start = timeToMinutes(meeting.startTimeStr ?? meeting.startTime) ?? timeToMinutes(PERIOD_TIMES[meeting.startPeriod]?.start)
    const end = timeToMinutes(meeting.endTimeStr ?? meeting.endTime) ?? timeToMinutes(PERIOD_TIMES[meeting.endPeriod ?? meeting.startPeriod]?.end)
    if (start != null && end != null && end > start) return [{ days, start, end }]
    // 워커(suwon-course-sync) 스냅샷 shape: { day, periods: [1,2,3] } — startPeriod/시각 문자열이
    // 없어 위 계산이 실패한다. 이 shape을 못 읽으면 실데이터 전체에서 충돌 감지가 무력화되므로
    // periods 배열을 연속 구간별 시간 범위로 복원한다.
    if (Array.isArray(meeting.periods) && meeting.periods.length) {
      return contiguousPeriodRuns(meeting.periods).flatMap(run => {
        const runStart = timeToMinutes(PERIOD_TIMES[run.start]?.start)
        const runEnd = timeToMinutes(PERIOD_TIMES[run.end]?.end)
        return runStart != null && runEnd != null && runEnd > runStart ? [{ days, start: runStart, end: runEnd }] : []
      })
    }
    return []
  })
}

export function getConflicts(selectedCourses, newCourse) {
  return selectedCourses.filter(c => checkTimeConflict(c, newCourse))
}

export function getAllConflicts(selectedCourses) {
  const pairs = []
  for (let i = 0; i < selectedCourses.length; i++) {
    for (let j = i + 1; j < selectedCourses.length; j++) {
      if (checkTimeConflict(selectedCourses[i], selectedCourses[j])) {
        pairs.push([selectedCourses[i], selectedCourses[j]])
      }
    }
  }
  return pairs
}

// 선택 결과에만 붙이는 파생 상태다. 원본 course/section 객체는 절대 수정하지 않는다.
export function getCourseConflictState(selectedCourses = []) {
  return selectedCourses.map((course, index) => {
    const conflictsWith = selectedCourses.filter((other, otherIndex) => otherIndex !== index && checkTimeConflict(course, other))
    return { course, conflictsWith, hasConflict: conflictsWith.length > 0 }
  })
}

// ── 학점 계산 ──────────────────────────────────────────────────────────────────
export function calculateCredits(selectedCourses) {
  const sum = f => selectedCourses.filter(f).reduce((s, c) => s + c.credit, 0)
  const total = sum(() => true)
  return {
    total,
    major:    sum(c => c.category === '전공필수' || c.category === '전공선택'),
    required: sum(c => c.category === '전공필수'),
    elective: sum(c => c.category === '전공선택'),
    general:  sum(c => ['교양선택', '교양필수', '핵심교양', '영역교양', '기초교양'].includes(c.category)),
    online:   sum(c => c.isOnline),
    etc:      sum(c => c.category === '일반선택'),
  }
}

export function calculateCategoryCredits(selectedCourses) {
  const result = {}
  for (const c of selectedCourses) {
    result[c.category] = (result[c.category] ?? 0) + c.credit
  }
  return result
}

// ── 추가 가능 여부 (모드 인식) ────────────────────────────────────────────────
export function canAddCourse(selectedCourses, newCourse, mode = 'final') {
  const maxCredit = mode === 'pre' ? CREDIT_RULES.preRegistrationMax : CREDIT_RULES.defaultMax
  const cr = calculateCredits(selectedCourses)
  const result = { canAdd: true, warnings: [], errors: [] }

  if (selectedCourses.some(c => c.id === newCourse.id)) {
    result.canAdd = false
    result.errors.push('이미 담은 과목입니다.')
    return result
  }

  // 같은 과목 다른 분반이 이미 담겼는지
  const sameSub = selectedCourses.find(c => c.subjectKey && c.subjectKey === newCourse.subjectKey)
  if (sameSub) {
    result.canAdd = false
    result.errors.push(`이미 ${sameSub.division ?? ''}분반이 담겼습니다.`)
    return result
  }

  // 학점 초과
  if (cr.total + newCourse.credit > maxCredit) {
    if (mode === 'final') {
      result.canAdd = false
      result.errors.push(`최대 ${maxCredit}학점 초과 (현재 ${cr.total}학점)`)
    } else {
      result.warnings.push(`최대 학점(${maxCredit}) 초과 — 예비수강 모드에서는 추가됩니다`)
    }
  }

  // 이러닝 학기당 1과목 제한
  if (newCourse.isOnline && cr.online >= 1) {
    result.canAdd = false
    result.errors.push('이러닝은 학기당 1과목만 선택할 수 있습니다.')
    return result
  }

  // 시간 충돌
  const conflicts = getConflicts(selectedCourses, newCourse)
  if (conflicts.length > 0) {
    const names = conflicts.map(c => c.name).join(', ')
    if (mode === 'final') {
      result.canAdd = false
      result.errors.push(`시간 충돌: ${names}`)
    } else {
      result.warnings.push(`시간 충돌 (예비수강 모드): ${names}`)
    }
  }

  return result
}

// ── 남은 학점 ─────────────────────────────────────────────────────────────────
export function getRemainingCredits(selectedCourses, maxCredit = CREDIT_RULES.defaultMax) {
  const total = selectedCourses.reduce((s, c) => s + c.credit, 0)
  return Math.max(0, maxCredit - total)
}

// ── 공강 요일 ─────────────────────────────────────────────────────────────────
export function getFreeDays(selectedCourses) {
  const busy = new Set()
  selectedCourses
    .filter(c => getCourseScheduleMode(c) !== 'online' && c.days?.length)
    .forEach(c => c.days.forEach(d => busy.add(d)))
  return GRID_DAYS.filter(d => !busy.has(d))
}

// ── 시간표 그리드 빌드 ─────────────────────────────────────────────────────────
export function buildGrid(selectedCourses) {
  const grid = Array.from({ length: MAX_PERIOD }, () => Array(5).fill(null))
  selectedCourses
    .filter(c => getCourseScheduleMode(c) !== 'online' && c.days?.length && c.startPeriod != null)
    .forEach(course => {
      course.days.forEach(day => {
        const dayIdx = GRID_DAYS.indexOf(day)
        if (dayIdx === -1) return
        for (let p = course.startPeriod; p <= course.endPeriod; p++) {
          const pi = p - 1
          grid[pi][dayIdx] = p === course.startPeriod
            ? { course, rowSpan: course.endPeriod - course.startPeriod + 1, skip: false }
            : { course, skip: true }
        }
      })
    })
  return grid
}

// ── 교시 → 시간 문자열 ─────────────────────────────────────────────────────────
export function periodToTime(start, end) {
  if (start == null) return null
  const s = PERIOD_TIMES[start]?.start ?? ''
  const e = PERIOD_TIMES[end]?.end ?? ''
  return `${s}~${e}`
}

export function periodLabel(start, end) {
  if (start == null) return null
  return start === end ? `${start}교시` : `${start}~${end}교시`
}

// ── 카테고리 색상 ──────────────────────────────────────────────────────────────
export function getCategoryColors(category) {
  const map = {
    '전공필수': { bg: 'bg-blue-50',   text: 'text-blue-800',   border: 'border-blue-200',   dot: 'bg-blue-500',   darkBg: 'bg-blue-900/30',   darkText: 'text-blue-300' },
    '전공선택': { bg: 'bg-purple-50', text: 'text-purple-800', border: 'border-purple-200', dot: 'bg-purple-500', darkBg: 'bg-purple-900/30', darkText: 'text-purple-300' },
    '교양필수': { bg: 'bg-green-50',  text: 'text-green-800',  border: 'border-green-200',  dot: 'bg-green-500',  darkBg: 'bg-green-900/30',  darkText: 'text-green-300' },
    '교양선택': { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200', dot: 'bg-orange-500', darkBg: 'bg-orange-900/30', darkText: 'text-orange-300' },
    '핵심교양': { bg: 'bg-teal-50',   text: 'text-teal-800',   border: 'border-teal-200',   dot: 'bg-teal-500',   darkBg: 'bg-teal-900/30',   darkText: 'text-teal-300' },
    '영역교양': { bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-200',  dot: 'bg-amber-500',  darkBg: 'bg-amber-900/30',  darkText: 'text-amber-300' },
    '기초교양': { bg: 'bg-lime-50',   text: 'text-lime-800',   border: 'border-lime-200',   dot: 'bg-lime-500',   darkBg: 'bg-lime-900/30',   darkText: 'text-lime-300' },
    '일반선택': { bg: 'bg-indigo-50', text: 'text-indigo-800', border: 'border-indigo-200', dot: 'bg-indigo-500', darkBg: 'bg-indigo-900/30', darkText: 'text-indigo-300' },
    '이러닝':   { bg: 'bg-gray-100',  text: 'text-gray-600',   border: 'border-gray-200',   dot: 'bg-gray-400',   darkBg: 'bg-gray-800',      darkText: 'text-gray-300' },
  }
  return map[category] ?? map['이러닝']
}

// ── AI 조언 (rule-based) ──────────────────────────────────────────────────────
export function getAiAdvice(selectedCourses, preferences = {}, requirement = null, mode = 'final') {
  const advice = []
  const cr = calculateCredits(selectedCourses)
  const maxCredit = mode === 'pre' ? CREDIT_RULES.preRegistrationMax : CREDIT_RULES.defaultMax
  const conflicts = getAllConflicts(selectedCourses)
  const freeDays = getFreeDays(selectedCourses)
  const { noClassDays = [], avoidMorning = false, preferOnline = false, keepLunch = false } = preferences

  if (requirement) {
    const done = requirement.requiredSubjects.filter(rs =>
      selectedCourses.some(c => c.subjectKey === rs.subjectKey)
    )
    if (done.length === requirement.requiredSubjects.length) {
      advice.push({ type: 'ok', message: `필수 과목 ${requirement.requiredSubjects.length}개를 모두 선택했어요. 👍` })
    } else {
      advice.push({ type: 'info', message: `필수 과목 ${requirement.requiredSubjects.length}개 중 ${done.length}개 선택됨. 나머지 분반을 골라보세요.` })
    }
  }

  if (cr.total === 0) {
    advice.push({ type: 'info', message: '아직 담은 과목이 없어요. 필수 과목 분반부터 선택해 보세요.' })
  } else {
    const remaining = maxCredit - cr.total
    advice.push({ type: 'info', message: `현재 ${cr.total}학점 · 최대 ${maxCredit}학점까지 ${remaining > 0 ? remaining + '학점 더' : '가득 찼어요'}.` })
    if (cr.total < (requirement?.minCredit ?? 15)) {
      advice.push({ type: 'warning', message: `최소 이수 권장 학점(${requirement?.minCredit ?? 15}학점)보다 적어요.` })
    }
  }

  if (preferOnline || cr.online === 0) {
    advice.push({ type: 'info', message: '이러닝 1~2과목을 추가하면 오프라인 부담을 줄일 수 있어요.' })
  }
  if (mode === 'final' && cr.online > 3) {
    advice.push({ type: 'warning', message: '본수강 시 이러닝 1과목 제한이 있습니다. 확인해 주세요.' })
  }

  if (freeDays.length > 0) {
    advice.push({ type: 'ok', message: `${freeDays.join('·')}요일 공강이에요. 👍` })
  } else if (selectedCourses.some(c => getCourseScheduleMode(c) !== 'online')) {
    advice.push({ type: 'info', message: '공강이 없어요. 이러닝 과목으로 오프라인 수업을 대체하면 공강을 만들 수 있어요.' })
  }

  noClassDays.forEach(day => {
    const clash = selectedCourses.filter(c => getCourseScheduleMode(c) !== 'online' && c.days?.includes(day))
    if (clash.length > 0) {
      advice.push({ type: 'warning', message: `${day}요일 공강을 원하지만 수업이 있어요: ${clash.map(c => c.name).join(', ')}` })
    }
  })

  if (avoidMorning) {
    const morning = selectedCourses.filter(c => getCourseScheduleMode(c) !== 'online' && c.startPeriod != null && c.startPeriod <= 2)
    if (morning.length > 0) {
      advice.push({ type: 'warning', message: `오전 1-2교시 수업이 있어요: ${morning.map(c => c.name).join(', ')}` })
    } else {
      advice.push({ type: 'ok', message: '오전 수업이 없어요. 👍' })
    }
  }

  if (keepLunch) {
    const lunch = selectedCourses.filter(c => getCourseScheduleMode(c) !== 'online' && c.startPeriod != null && c.startPeriod <= 4 && c.endPeriod >= 4)
    if (lunch.length > 0) {
      advice.push({ type: 'warning', message: `점심시간(4교시)에 수업이 있어요: ${lunch.map(c => c.name).join(', ')}` })
    } else {
      advice.push({ type: 'ok', message: '점심시간이 확보되어 있어요. 👍' })
    }
  }

  conflicts.forEach(([a, b]) => {
    advice.push({ type: 'error', message: `⛔ 시간 충돌: "${a.name}"과 "${b.name}"이 겹쳐요.` })
  })

  return advice
}

// ── 플랜B 대체 과목 ────────────────────────────────────────────────────────────
/**
 * @param {object[]} selectedCourses
 * @param {string[]} targetCourseIds
 * @param {string|null} selectedDept  사용자 학과 (학과 필터 우선순위에 사용)
 */
export function getPlanBOptions(selectedCourses, targetCourseIds, selectedDept = null, { grade = null, college = null } = {}) {
  const eligibleTargetIds = targetCourseIds.filter(id => {
    const course = selectedCourses.find(c => c.id === id)
    return course && !isPlanBFixedCourse(course)
  })
  const succeeded = selectedCourses.filter(c => !eligibleTargetIds.includes(c.id))

  // 학과 기준 태그 우선순위: my-dept=0, open=1, online=2, unknown=3, other-dept=4
  const TAG_PRI = { 'my-dept': 0, 'open': 1, 'online': 2, 'unknown': 3, 'other-dept': 4 }

  // 학년/학과(학과 없으면 단과대) 기준 canonical eligibility — 추천 엔진·분반 설정과 동일한
  // getEligibleSectionsForStudent로 후보 pool 자체를 좁힌다(정렬용 태그와 별개의 hard filter).
  const eligiblePool = getEligibleSectionsForStudent(getRuntimeCourseCatalog(), { college, department: selectedDept, grade })

  return eligibleTargetIds.map(targetId => {
    const target = selectedCourses.find(c => c.id === targetId)
    if (!target) return { target: null, alternatives: [] }

    const sameDivAlts = []   // 같은 과목 다른 분반
    const sameCatAlts = []   // 같은 카테고리 대체

    // 후보 정렬 기준: 현재 등교 요일에 새 요일을 추가하지 않는 후보(공강 유지) 우선,
    // 그다음 기존 수업 시간과 가까운 순 — 새 scoring 엔진 없이 기존 요일/교시 필드만 재사용한다.
    const attendingDays = new Set(succeeded.flatMap(s => s.days ?? []))
    function rankOf(c) {
      const newDays = (c.days ?? []).filter(d => !attendingDays.has(d)).length
      const timeDistance = (c.startPeriod != null && target.startPeriod != null)
        ? Math.abs(c.startPeriod - target.startPeriod)
        : 99
      return { newDays, timeDistance }
    }

    for (const c of eligiblePool) {
      if (c.id === targetId) continue
      if (eligibleTargetIds.includes(c.id)) continue
      if (selectedCourses.some(s => s.id === c.id)) continue
      if (getConflicts(succeeded, c).length > 0) continue

      const tag      = getCourseTag(c, selectedDept)
      const tagPri   = TAG_PRI[tag] ?? 4
      const deptNote = getDeptReasonLabel(c, selectedDept)
      const isSameSubject = Boolean(target.subjectKey && c.subjectKey === target.subjectKey)
      if (isPlanBFixedCourse(c)) continue

      const { newDays, timeDistance } = rankOf(c)
      if (isSameSubject) {
        const reasonParts = ['같은 과목 다른 분반 — 시간 충돌 없음']
        if (deptNote) reasonParts.push(deptNote)
        sameDivAlts.push({ ...c, _reason: reasonParts.join(' · '), _tagPri: tagPri, _newDays: newDays, _timeDistance: timeDistance })
      } else if (c.category === target.category && tag !== 'other-dept') {
        // 타 학과 제한 분반은 교양 대체에서 기본 제외
        const reasonParts = [`같은 카테고리(${c.category}) 대체 과목`]
        if (deptNote) reasonParts.push(deptNote)
        sameCatAlts.push({ ...c, _reason: reasonParts.join(' · '), _tagPri: tagPri, _newDays: newDays, _timeDistance: timeDistance })
      }
    }

    // 공강 유지 > 기존 수업 시간과 근접 > 학과 우선순위 순으로 정렬
    const byRank = (a, b) => (a._newDays - b._newDays) || (a._timeDistance - b._timeDistance) || (a._tagPri - b._tagPri)
    sameDivAlts.sort(byRank)
    sameCatAlts.sort(byRank)

    // 후보 목록 자체는 자르지 않는다 — PlanBScreen의 바텀시트가 검색으로 걸러 보여준다.
    const sameSubjectAlternatives = sameDivAlts
    const similarAlternatives = sameCatAlts
    return {
      target,
      sameSubjectAlternatives,
      similarAlternatives,
      alternatives: [...sameSubjectAlternatives, ...similarAlternatives].slice(0, 4),
    }
  })
}

// ── 플랜B: 완전한 대체 시간표 3개 생성 ────────────────────────────────────────
/**
 * 지정한 과목들을 교체해 완성된 대체 시간표 최대 3개를 반환한다.
 * @param {string[]} targetIds     교체 대상 과목 id 배열
 * @param {string|null} department 학과 (학과 필터 우선순위용)
 * @returns {{ schedule, replacements, credits, freeDays, label, score, reasons }[]}
 */
export function generateCompletePlanBSchedules(baseSchedule, targetIds, department = null, { grade = null, college = null } = {}) {
  const eligibleTargetIds = targetIds.filter(id => {
    const course = baseSchedule.find(c => c.id === id)
    return course && !isPlanBFixedCourse(course)
  })
  const succeeded    = baseSchedule.filter(c => !eligibleTargetIds.includes(c.id))
  const targetCourses = baseSchedule.filter(c => eligibleTargetIds.includes(c.id))

  if (targetCourses.length === 0) return []

  const originalFreeDays = getFreeDays(baseSchedule)
  const originalCredits  = baseSchedule.reduce((s, c) => s + c.credit, 0)
  const TAG_PRI = { 'my-dept': 0, 'open': 1, 'online': 2, 'unknown': 3, 'other-dept': 4 }

  // 학년/학과(학과 없으면 단과대) 기준 canonical eligibility — getPlanBOptions와 동일하게 적용.
  const eligiblePool = getEligibleSectionsForStudent(getRuntimeCourseCatalog(), { college, department, grade })

  // 교체 대상별 후보 풀 구성 (충돌 없는 과목만)
  const pools = targetCourses.map(target => {
    const sameDiv = [], sameCat = []
    for (const c of eligiblePool) {
      if (eligibleTargetIds.includes(c.id)) continue
      if (baseSchedule.some(b => b.id === c.id)) continue
      if (getCourseScheduleMode(c) !== 'online' && getConflicts(succeeded, c).length > 0) continue
      const tag    = getCourseTag(c, department)
      const tagPri = TAG_PRI[tag] ?? 4
      const isSameSubject = Boolean(target.subjectKey && c.subjectKey === target.subjectKey)
      if (isPlanBFixedCourse(c)) continue
      if (isSameSubject) {
        sameDiv.push({ course: c, priority: 0, tagPri })
      } else if (c.category === target.category && tag !== 'other-dept') {
        sameCat.push({ course: c, priority: 1, tagPri })
      }
    }
    sameDiv.sort((a, b) => a.tagPri - b.tagPri)
    sameCat.sort((a, b) => a.tagPri - b.tagPri)
    return { target, pool: [...sameDiv, ...sameCat].slice(0, 12) }
  })

  // 3가지 전략으로 조합 생성 (오프셋 변화)
  const results = []
  for (let offset = 0; offset < 4 && results.length < 3; offset++) {
    let current     = [...succeeded]
    const replaced  = []
    let valid       = true

    for (const { target, pool } of pools) {
      let found = null
      for (let i = 0; i < pool.length; i++) {
        const cand = pool[(i + offset) % pool.length]
        if (!cand) break
        if (current.some(c => c.id === cand.course.id)) continue
        if (getCourseScheduleMode(cand.course) !== 'online' && getConflicts(current, cand.course).length > 0) continue
        found = cand
        break
      }
      if (found) {
        current.push(found.course)
        replaced.push({
          original:    target,
          replacement: found.course,
          reason:      found.priority === 0 ? '같은 과목 다른 분반' : `같은 이수구분(${target.category}) 대체`,
        })
      } else {
        // 이 대상 과목에 대체 없음 → 해당 대상 과목 제외하고 계속
        valid = false
      }
    }

    if (replaced.length === 0) continue

    // 이미 동일한 schedule 있으면 skip
    const key = current.map(c => c.id).sort().join(',')
    if (results.some(r => r._key === key)) continue

    const freeDays  = getFreeDays(current)
    const credits   = current.reduce((s, c) => s + c.credit, 0)
    const freeMatch = originalFreeDays.filter(d => freeDays.includes(d)).length
    const sameSubjectCount = replaced.filter(r => r.reason.includes('다른 분반')).length

    // 점수화
    let score = 0
    score += freeMatch * 60                                       // 공강 유지
    score -= Math.abs(credits - originalCredits) * 25            // 학점 차이 패널티
    score += sameSubjectCount * 100                               // 같은 과목 우선
    score += (valid ? 30 : 0)                                     // 모든 실패 과목 대체 보너스

    const label = sameSubjectCount === replaced.length && replaced.length > 0
      ? '같은 과목 다른 분반형'
      : freeMatch === originalFreeDays.length && originalFreeDays.length > 0
        ? '공강 유지형'
        : Math.abs(credits - originalCredits) <= 1
          ? '학점 유지형'
          : '안정형'

    results.push({
      schedule:     current,
      replacements: replaced,
      credits,
      freeDays,
      label,
      score,
      _key: key,
      reasons: [
        `총 ${credits}학점 (기존 ${originalCredits}학점)`,
        freeDays.length > 0 ? `공강 ${freeDays.join('·')}요일` : '공강 없음',
        ...replaced.map(r => `${r.original.name} → ${r.replacement.name}`),
      ],
    })
  }

  results.sort((a, b) => b.score - a.score)
  return results.slice(0, 3)
}

// ── 데이터 정보 ────────────────────────────────────────────────────────────────
export function getDataInfo() {
  const meta = getRuntimeCatalogMeta()
  return { date: meta?.publishedAt, source: meta?.source, total: getRuntimeCourseCatalog().length }
}

// ── 시간표 과목 색상 팔레트 (12색, 파스텔 톤) ────────────────────────────────
export const COURSE_COLOR_PALETTE = [
  { id: 'blue',   label: '파랑', bg: '#EAF2FD', text: '#1B5FA0', border: '#BAD5F7', meta: 'rgba(27,95,160,0.8)'  },
  { id: 'green',  label: '초록', bg: '#E4F7ED', text: '#1A6847', border: '#A6DABA', meta: 'rgba(26,104,71,0.8)'  },
  { id: 'rose',   label: '분홍', bg: '#FDEAEF', text: '#72243E', border: '#EFBAC9', meta: 'rgba(114,36,62,0.8)'  },
  { id: 'purple', label: '보라', bg: '#EFEEFD', text: '#3C3489', border: '#C7C3F2', meta: 'rgba(60,52,137,0.8)'  },
  { id: 'yellow', label: '노랑', bg: '#FDF4E3', text: '#7A4C10', border: '#EDD089', meta: 'rgba(122,76,16,0.8)'  },
  { id: 'teal',   label: '청록', bg: '#E3F6F7', text: '#1A6468', border: '#A4D9DC', meta: 'rgba(26,100,104,0.8)' },
  { id: 'orange', label: '주황', bg: '#FDEEE3', text: '#7C3C14', border: '#EFBB96', meta: 'rgba(124,60,20,0.8)'  },
  { id: 'pink',   label: '핑크', bg: '#FDE8F5', text: '#6B1A4F', border: '#EDAAD4', meta: 'rgba(107,26,79,0.8)'  },
  { id: 'lime',   label: '연두', bg: '#EBF5E0', text: '#2D6A00', border: '#B3D98C', meta: 'rgba(45,106,0,0.8)'   },
  { id: 'indigo', label: '남색', bg: '#EAECFD', text: '#2B2F9A', border: '#BACAF7', meta: 'rgba(43,47,154,0.8)'  },
  { id: 'amber',  label: '황금', bg: '#FDF5E0', text: '#7A5310', border: '#EDD880', meta: 'rgba(122,83,16,0.8)'  },
  { id: 'slate',  label: '회청', bg: '#EAEEF3', text: '#3A4A5C', border: '#B4C4D0', meta: 'rgba(58,74,92,0.8)'   },
]

export const COURSE_COLOR_OPTIONS = [...SMART_COURSE_PALETTE, ...COURSE_COLOR_PALETTE]
const PALETTE_ID_SET = new Set(COURSE_COLOR_OPTIONS.map(c => c.id))
const LEGACY_HEX_TO_PALETTE_ID = new Map([
  ['#16a34a', 'green'],  ['#15803d', 'green'],
  ['#0f766e', 'teal'],   ['#0d9488', 'teal'],
  ['#d97706', 'orange'], ['#b45309', 'orange'],
  ['#0284c7', 'blue'],   ['#0369a1', 'blue'],  ['#475569', 'blue'],
  ['#7c3aed', 'purple'], ['#6d28d9', 'purple'], ['#5b21b6', 'purple'],
  ['#ec4899', 'rose'],   ['#db2777', 'rose'],
  ['#be185d', 'rose'],   ['#9d174d', 'rose'],  ['#dc2626', 'rose'],
])

/** 저장된 hex 또는 팔레트 ID를 팔레트 ID로 정규화 */
export function resolvePaletteId(raw) {
  if (!raw) return COURSE_COLOR_PALETTE[0].id
  const n = String(raw).trim().toLowerCase()
  if (PALETTE_ID_SET.has(n)) return n
  return LEGACY_HEX_TO_PALETTE_ID.get(n) ?? COURSE_COLOR_PALETTE[0].id
}

/** 팔레트 ID로 팔레트 항목 반환 (없으면 첫번째) */
export function getPaletteEntry(id) {
  return COURSE_COLOR_OPTIONS.find(c => c.id === (id ?? '')) ?? COURSE_COLOR_PALETTE[0]
}

/** 과목 id(course.id) → 팔레트 항목. 등장 순서대로 팔레트를 순환 배정한다 (TimetableGrid와 동일 규칙). */
export function assignCourseColors(courses = []) {
  const map = new Map()
  const sourceColors = new Map()
  courses.filter(c => getCourseScheduleMode(c) !== 'online').forEach(c => {
    const key = c.stableCourseId ?? c.id
    if (!sourceColors.has(key)) sourceColors.set(key, COURSE_COLOR_PALETTE[sourceColors.size % COURSE_COLOR_PALETTE.length])
    map.set(c.id, sourceColors.get(key))
  })
  return map
}

// ── 하위 호환 alias (기존 컴포넌트가 참조할 경우 대비) ─────────────────────────
export const calcCredits       = calculateCredits
export const calcFreeDays      = getFreeDays
export const hasTimeConflict   = checkTimeConflict
export const getConflictsWith  = getConflicts
export const generatePlanBAlternatives = getPlanBOptions
